/**
 * Save format v3 (Phase 2). JSON snapshot to localStorage; downloadable
 * .json export. Both the load path and the import path run migrateSave(),
 * so v1/v2 saves upgrade to the fixed container layouts transparently.
 */
import type { PlayerState } from '../state';
import { SKILLS_DATA } from '../../data';
import { emptyBank, emptyEquipment, emptyInventory } from '../economy/containers';
import { migrateSave, SAVE_VERSION } from './migrate';

export { SAVE_VERSION };
export const SAVE_KEY = 'hermanity-idle::save::v4';
/** Older keys still read (and migrated) on load. */
const LEGACY_SAVE_KEYS = [
  'hermanity-idle::save::v3',
  'hermanity-idle::save::v2',
  'hermanity-idle::save::v1',
] as const;

export class UnsupportedFutureSaveError extends Error {
  constructor(readonly storedVersion: number) {
    super(`Save version ${storedVersion} is newer than supported version ${SAVE_VERSION}.`);
    this.name = 'UnsupportedFutureSaveError';
  }
}

function storedFutureVersion(): number | null {
  const raw = localStorage.getItem(SAVE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { version?: unknown };
    return typeof parsed?.version === 'number' && parsed.version > SAVE_VERSION
      ? parsed.version
      : null;
  } catch {
    return null;
  }
}

export function unsupportedFutureSaveVersion(): number | null {
  return storedFutureVersion();
}

export function emptyState(): PlayerState {
  // Build skills map from validated catalogue
  const skills = Object.fromEntries(
    SKILLS_DATA.skills.map((s) => [s.id, { level: 1, xp: 0 }]),
  ) as PlayerState['skills'];

  return {
    version: SAVE_VERSION,
    savedAt: new Date(0).toISOString(),
    checksum: '',
    player: { name: 'Herman', mannerPoints: 0 },
    skills,
    bank: emptyBank(),
    equipment: emptyEquipment(),
    settings: {
      theme: 'dark',
      soundVolume: 0.6,
      musicVolume: 0.4,
      combatOffline: true,
      tokenMiningOffline: true,
    },
    active: { kind: 'idle' },
    quests: [],
    flags: [],
    combatRound: 0,
    inventory: emptyInventory(),
    bankTab: 0,
    lastSimulatedAt: new Date(0).toISOString(),
    actionProgressMs: 0,
    hp: { player: 100, monster: null },
  };
}

/** Stable JSON for hashing (sorted keys). */
function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(obj[k])}`).join(',')}}`;
}

/** SHA-256 hex via SubtleCrypto (browser only). */
async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Stamp the state with savedAt + lastSimulatedAt + checksum and persist.
 * The online loop keeps the simulation current, so the save moment is also
 * the simulated-up-to moment.
 */
export async function saveToLocalStorage(state: PlayerState): Promise<PlayerState> {
  const futureVersion = storedFutureVersion();
  if (futureVersion !== null) throw new UnsupportedFutureSaveError(futureVersion);
  const now = new Date().toISOString();
  const stamped: PlayerState = {
    ...state,
    version: SAVE_VERSION,
    savedAt: now,
    lastSimulatedAt: now,
    checksum: '',
  };
  const csum = await sha256Hex(canonical(stamped).replace('"checksum":""', '"checksum":"\\u0000"'));
  const final: PlayerState = { ...stamped, checksum: csum };
  localStorage.setItem(SAVE_KEY, JSON.stringify(final));
  return final;
}

/** Read + parse + migrate. Returns null if no loadable save exists. */
export async function loadFromLocalStorage(): Promise<PlayerState | null> {
  for (const key of [SAVE_KEY, ...LEGACY_SAVE_KEYS]) {
    const raw = localStorage.getItem(key);
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw) as { version?: unknown };
      if (key === SAVE_KEY && typeof parsed?.version === 'number' && parsed.version > SAVE_VERSION) {
        console.warn(
          `[Hermanity Idle] current save is version ${parsed.version}; this build supports ${SAVE_VERSION}. Preserving it without fallback or overwrite.`,
        );
        return null;
      }
      const migrated = migrateSave(parsed);
      if (migrated) return migrated;
      console.warn(`[Hermanity Idle] save at ${key} is unsupported, trying older keys`);
    } catch (err) {
      console.warn(`[Hermanity Idle] save load failed for ${key}:`, err);
    }
  }
  return null;
}

/** Export save as a downloadable .json Blob URL. */
export function exportSaveAsBlob(state: PlayerState): Blob {
  return new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
}

/**
 * Reverse: import a save from a string. Runs the same migration chain as
 * the local load path. Returns null on parse/migration failure.
 */
export function importSaveFromString(raw: string): PlayerState | null {
  try {
    return migrateSave(JSON.parse(raw));
  } catch {
    return null;
  }
}
